import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import debounce from "lodash/debounce";
import ReactModal from "react-modal";
import Icon from "backpack-ui/dist/components/icon";
import { getEnv } from "shared/utils";
import Overlay from "shared/components/Overlay";
import Input from "../Input";
import Results from "../Results";
import { getAutocomplete } from "../service";
import styles from "./styles.css";

class Modal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Modal is rendered outside the root element, so we need "offsetTop" to
      // position it along, when ads push whole GlobalHeader down.
      offsetTop: 0,
      placeholder: "Search Lonely Planet",
      searchResults: [],
      searchTerm: null,
      isVisible: false,
      isFetching: false,
    };

    this.resultsCache = {};

    this.debouncedFetch = debounce(this.fetchResults, 250);
    this.debouncedUpdatePlaceholder = debounce(this.updatePlaceholder, 200);
  }

  componentDidMount() {
    this.headerNode = null;

    if (typeof document !== "undefined") {
      // "offsetTop" reference element
      this.headerNode = document.getElementById("globalHeader");
    }

    if (this.headerNode) {
      // Required by ReactModal component
      ReactModal.setAppElement(this.headerNode);
    }

    this.updatePlaceholder();
    window.addEventListener("resize", this.debouncedUpdatePlaceholder);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedUpdatePlaceholder);
  }

  getOffsetTop = () => {
    let offsetTop = 0;

    if (this.headerNode) {
      offsetTop = this.headerNode.getBoundingClientRect().top;
    }
    return offsetTop;
  };

  updatePlaceholder = () => {
    this.setState({
      placeholder:
        window.innerWidth < 768
          ? "Search Lonely Planet"
          : "Search places, cities, countries and continents",
    });
  };

  show = () => {
    this.setState({
      isVisible: true,
      offsetTop: this.getOffsetTop(),
    });
  };

  hide = () => {
    const { onRequestClose } = this.props;

    this.setState(
      {
        searchTerm: "",
        searchResults: [],
        isVisible: false,
      },
      onRequestClose
    );
  };

  handleInput = (event) => {
    const searchTerm = event.target.value.trim();
    const { searchTerm: currSearchTerm } = this.state;

    // Early return if search term haven't changed
    if (searchTerm === currSearchTerm) return;

    this.setState({ searchTerm }, () => {
      this.debouncedFetch(searchTerm);
    });
  };

  async fetchResults(searchTerm) {
    let searchResults = [];

    // Early return if search term is falsy
    if (!searchTerm) {
      this.setState({ searchResults, isFetching: false });
      return;
    }

    // Get cached results if available
    if (Object.prototype.hasOwnProperty.call(this.resultsCache, searchTerm)) {
      searchResults = this.resultsCache[searchTerm];
    } else {
      this.setState({ isFetching: true });
      searchResults = await getAutocomplete(searchTerm, 5);
    }

    const { searchTerm: currSearchTerm } = this.state;

    // Update state for correct term (race condition fix for late requests)
    if (searchTerm === currSearchTerm) {
      this.setState({ isFetching: false, searchResults });
      this.resultsCache[searchTerm] = searchResults;
    }
  }

  render() {
    const { isOpen } = this.props;
    const {
      offsetTop,
      placeholder,
      searchResults,
      searchTerm,
      isFetching,
      isVisible,
    } = this.state;

    return (
      <ReactModal
        isOpen={isOpen}
        overlayClassName={styles.modalOverlay}
        style={{
          overlay: { top: `${offsetTop}px` } /* stylelint-disable-line */,
        }}
        className={cn({
          [styles.wrapper]: true,
          [styles.isVisible]: isVisible,
        })}
        shouldCloseOnOverlayClick={false}
        onRequestClose={this.hide}
        onAfterOpen={this.show}
        closeTimeoutMS={400}
      >
        <Overlay
          onClick={this.hide}
          style={{ top: `${offsetTop}px` }}
          className={styles.contentOverlay}
        />

        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.inner}>
              <form action={getEnv("SEARCH_URL")}>
                {isFetching ? (
                  <Icon.Loading
                    label="Loading"
                    className={styles.loadingIcon}
                  />
                ) : (
                  <Icon.Search label="Search" className={styles.searchIcon} />
                )}
                <Input
                  name="q"
                  className={styles.input}
                  placeholder={placeholder}
                  tabIndex="0"
                  autoFocus
                  onInput={this.handleInput}
                />
                <input type="submit" hidden />
                <button
                  type="button"
                  title="Close search"
                  className={styles.closeButton}
                  onClick={this.hide}
                  tabIndex="0"
                >
                  <Icon.Close className={styles.closeIcon} />
                </button>
              </form>
            </div>
            {searchResults.length > 0 && (
              <Results
                data={searchResults}
                searchTerm={searchTerm}
                className={styles.results}
              />
            )}
          </div>
        </div>
      </ReactModal>
    );
  }
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

Modal.defaultProps = {};

export default Modal;
